import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useReducer,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { getGlobalTasks } from "src/redux/creator-database/creatorDatabaseActions";
import { Rectangle } from "@glideapps/glide-data-grid";
import { tableDataType } from "src/app/components/Table/Table";
import { uploadFileBasingOnContext } from "src/app/metafields/FileField/taskFunctions";
import { useParams } from "react-router";
import { showToast } from "src/app/methods/showToast";
import { ChangeWillAffectManyRowsModal } from "src/app/modals/ChangeWillAffectManyRowsModal/ChangeWillAffectManyRowsModal";
import { Row } from "src/redux/dictionary/types";
import { GlideDataGridContext, StaticCellSettings } from "../../../types";
import { getCellPosition, initialStaticCellSettings } from "../../../utils";
import { RootState } from "../../../../../../redux/reducers";
import creatorDatabaseFiltersReducer, {
  creatorDatabaseFiltersReducerInitialState,
} from "../../../../../CreatorDatabase/redux/creatorDatabaseFiltersReducer";
import { getDictionaryListElement } from "../../../../../../redux";

interface EmptyFileCellProps {
  uuid: string;
  rowUuid: string;
  closeDropdown: () => void;
  setStaticCellSettings: Dispatch<SetStateAction<StaticCellSettings>>;
  bounds: Rectangle;
  wsWorkspaceUuid: string;
  context: GlideDataGridContext;
  selectedRowsData?: Row[];
  fieldName?: string;
  isUniqueField?: boolean;
}

function EmptyFileCell({
  uuid,
  rowUuid,
  closeDropdown,
  setStaticCellSettings,
  bounds,
  wsWorkspaceUuid,
  context,
  selectedRowsData,
  fieldName,
  isUniqueField,
}: EmptyFileCellProps) {
  const dispatch = useDispatch();
  const {
    creatorDatabaseReducer: { globalTaskColumns },
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);
  const params = useParams<{ dictionaryUuid: string }>();
  const [pendingUploadEvent, setPendingUploadEvent] =
    useState<ChangeEvent<HTMLInputElement> | null>(null);

  const [creatorDatabaseFilters] = useReducer(
    creatorDatabaseFiltersReducer,
    creatorDatabaseFiltersReducerInitialState(globalTaskColumns),
  );
  const { filters, sortBy, sort } = creatorDatabaseFilters;

  const isGlobalTask = context === tableDataType.CreatorDatabase;

  const uploadToSingleRow = async (
    e: ChangeEvent<HTMLInputElement>,
    targetRowUuid: string,
  ) => {
    await uploadFileBasingOnContext({
      e,
      wsMetaFieldUuid: uuid,
      wsWorkspaceUuid,
      wsTaskUuid: targetRowUuid,
      thenCallback: () => {
        if (context === tableDataType.Dictionary) {
          dispatch(
            getDictionaryListElement(params.dictionaryUuid, targetRowUuid),
          );
        }
      },
      context: isGlobalTask ? tableDataType.GlobalTask : context,
    });
  };

  const uploadToMultipleRows = async (e: ChangeEvent<HTMLInputElement>) => {
    setStaticCellSettings({
      numberOfFiles: e.target.files?.length ?? 0,
      style: getCellPosition(bounds),
    });
    try {
      for (const row of selectedRowsData || []) {
        // eslint-disable-next-line no-await-in-loop
        await uploadToSingleRow(e, row.uuid);
      }
      if (isGlobalTask) {
        dispatch(getGlobalTasks(wsWorkspaceUuid, { filters, sortBy, sort }));
      }
    } finally {
      closeDropdown();
      setStaticCellSettings(initialStaticCellSettings);
    }
  };

  const uploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    const isPartOfMultiSelection =
      (selectedRowsData?.length || 0) > 1 &&
      selectedRowsData?.some((row) => row.uuid === rowUuid);

    if (isPartOfMultiSelection) {
      if (context === tableDataType.Dictionary && isUniqueField) {
        showToast(
          "error",
          "Error",
          "This field must be unique and can't be bulk-updated for multiple rows.",
        );
        return;
      }
      setPendingUploadEvent(e);
      return;
    }

    setStaticCellSettings({
      numberOfFiles: e.target.files?.length ?? 0,
      style: getCellPosition(bounds),
    });
    uploadFileBasingOnContext({
      e,
      wsMetaFieldUuid: uuid,
      wsWorkspaceUuid,
      wsTaskUuid: rowUuid,
      thenCallback: () => {
        closeDropdown();
        if (isGlobalTask) {
          dispatch(getGlobalTasks(wsWorkspaceUuid, { filters, sortBy, sort }));
        } else if (context === tableDataType.Dictionary) {
          dispatch(getDictionaryListElement(params.dictionaryUuid, rowUuid));
        }
      },
      finallyCallback: () => {
        setStaticCellSettings(initialStaticCellSettings);
      },
      context: isGlobalTask ? tableDataType.GlobalTask : context,
    });
  };

  const confirmMultiUpload = () => {
    const event = pendingUploadEvent;
    setPendingUploadEvent(null);
    if (event) {
      uploadToMultipleRows(event);
    }
  };

  const cancelMultiUpload = () => {
    setPendingUploadEvent(null);
    closeDropdown();
  };

  return (
    <>
      <input
        type="file"
        className="glide-data-grid__empty-file-cell"
        multiple
        onChange={(e) => uploadFile(e)}
      />

      {pendingUploadEvent && (
        <ChangeWillAffectManyRowsModal
          onClose={cancelMultiUpload}
          fieldName={fieldName || ""}
          rowsNumber={selectedRowsData?.length || 0}
          onConfirmClick={confirmMultiUpload}
          onCancelClick={cancelMultiUpload}
        />
      )}
    </>
  );
}

export default EmptyFileCell;
